import React, { useCallback, useContext, useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth0 } from "@auth0/auth0-react";
import { useViewPermission } from "../context/PermissionContext";
import NotificationContext from "../context/NotificationContext";
import Loader from "../components/Loader";
import AccessDenied from "../components/AccessDenied";
import { useMutationLock } from "../hooks/useMutationLock";
import { normalizeListResponse } from "../utils/listPagination";
import * as rolesApi from "../api/roles";
import * as permissionsApi from "../api/permissions";
import * as rolePermissionsApi from "../api/rolePermissions";

const emptyRoleForm = {
  name: "",
  label: "",
  description: "",
  userType: "admin",
};

const emptyPermissionForm = {
  name: "",
  label: "",
  description: "",
  userType: "admin",
};

export default function AdminRolePermissions() {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const notificationCtx = useContext(NotificationContext);

  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [rolePermissions, setRolePermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const { hasPermission, loading: permLoading } = useViewPermission(
    "view_role_permissions",
  );
  const { hasPermission: canEdit } = useViewPermission(
    "edit_role_permissions",
  );
  const { busy: roleFormBusy, run: runRoleFormMutation } = useMutationLock();
  const { busy: permissionFormBusy, run: runPermissionFormMutation } =
    useMutationLock();

  const [roleForm, setRoleForm] = useState(emptyRoleForm);
  const [permissionForm, setPermissionForm] = useState(emptyPermissionForm);

  const [editingRoleId, setEditingRoleId] = useState(null);
  const [editingPermissionId, setEditingPermissionId] = useState(null);

  // Permission assignment modal
  const [permissionModalOpen, setPermissionModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [savingPermissions, setSavingPermissions] = useState(false);

  const fetchAllData = useCallback(async () => {
    setLoading(true);

    try {
      const [rolesRes, permissionsRes, rolePermissionsRes] = await Promise.all([
        rolesApi.list({ exportMode: true }),
        permissionsApi.list({ exportMode: true }),
        rolePermissionsApi.list({ exportMode: true }),
      ]);

      setRoles(normalizeListResponse(rolesRes.data).data);
      setPermissions(normalizeListResponse(permissionsRes.data).data);
      setRolePermissions(normalizeListResponse(rolePermissionsRes.data).data);
    } catch (error) {
      console.error("Error fetching role permission data:", error);

      notificationCtx.show({
        message: "Failed to load role and permission records.",
        stat: "error",
      });
    } finally {
      setLoading(false);
    }
  }, [notificationCtx]);

  useEffect(() => {
    if (!hasPermission) return;

    fetchAllData();
  }, [hasPermission, fetchAllData]);

  const handleRoleFieldChange = (e) => {
    const { name, value } = e.target;

    setRoleForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePermissionFieldChange = (e) => {
    const { name, value } = e.target;

    setPermissionForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetRoleForm = () => {
    setRoleForm(emptyRoleForm);
    setEditingRoleId(null);
  };

  const resetPermissionForm = () => {
    setPermissionForm(emptyPermissionForm);
    setEditingPermissionId(null);
  };

  const handleRoleSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    await runRoleFormMutation(async () => {
      const payload = {
        name: roleForm.name.trim(),
        label: roleForm.label.trim(),
        description: roleForm.description.trim(),
        userType: roleForm.userType || "admin",
      };

      if (!payload.name || !payload.label || !payload.description) {
        notificationCtx.show({
          message: "Please fill in all role fields.",
          stat: "error",
        });

        return;
      }

      try {
        if (editingRoleId) {
          await rolesApi.update(editingRoleId, payload);

          notificationCtx.show({
            message: "Role updated successfully.",
            stat: "success",
          });
        } else {
          await rolesApi.create(payload);

          notificationCtx.show({
            message: "Role added successfully.",
            stat: "success",
          });
        }

        resetRoleForm();
        await fetchAllData();
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to save role.";

        notificationCtx.show({
          message,
          stat: "error",
        });
      }
    });
  };

  const handlePermissionSubmit = async (e) => {
    e.preventDefault();
    if (!canEdit) return;

    await runPermissionFormMutation(async () => {
      const payload = {
        name: permissionForm.name.trim(),
        label: permissionForm.label.trim(),
        description: permissionForm.description.trim(),
        userType: permissionForm.userType || "admin",
      };

      if (!payload.name || !payload.label || !payload.description) {
        notificationCtx.show({
          message: "Please fill in all permission fields.",
          stat: "error",
        });

        return;
      }

      try {
        if (editingPermissionId) {
          await permissionsApi.update(editingPermissionId, payload);

          notificationCtx.show({
            message: "Permission updated successfully.",
            stat: "success",
          });
        } else {
          await permissionsApi.create(payload);

          notificationCtx.show({
            message: "Permission added successfully.",
            stat: "success",
          });
        }

        resetPermissionForm();
        await fetchAllData();
      } catch (error) {
        const message =
          error?.response?.data?.message || "Failed to save permission.";

        notificationCtx.show({
          message,
          stat: "error",
        });
      }
    });
  };

  const handleEditRole = (role) => {
    setEditingRoleId(role.id);

    setRoleForm({
      name: role.name || "",
      label: role.label || "",
      userType: role.userType || "admin",
      description: role.description || "",
    });
  };

  const handleEditPermission = (permission) => {
    setEditingPermissionId(permission.id);

    setPermissionForm({
      name: permission.name || "",
      label: permission.label || "",
      userType: permission.userType || "admin",
      description: permission.description || "",
    });
  };

  /*
   * Open permission assignment modal.
   *
   * Only permissions belonging to the role's userType are shown.
   * Existing mappings are pre-selected.
   */
  const handleOpenPermissionModal = (role) => {
    const assignedPermissionIds = rolePermissions
      .filter(
        (mapping) => mapping.roleId === role.id && mapping.isActive !== false,
      )
      .map((mapping) => mapping.permissionId);

    setSelectedRole(role);
    setSelectedPermissionIds(assignedPermissionIds);
    setPermissionModalOpen(true);
  };

  const handleClosePermissionModal = () => {
    if (savingPermissions) return;

    setPermissionModalOpen(false);
    setSelectedRole(null);
    setSelectedPermissionIds([]);
  };

  const handlePermissionCheckboxChange = (permissionId) => {
    setSelectedPermissionIds((prev) => {
      if (prev.includes(permissionId)) {
        return prev.filter((id) => id !== permissionId);
      }

      return [...prev, permissionId];
    });
  };

  /*
   * Synchronizes the selected checkboxes with the existing
   * role-permission mappings.
   *
   * Existing active mappings that are unchecked are deleted.
   * Existing inactive mappings that are checked are re-enabled.
   * Missing mappings that are checked are created.
   */
  const handleSaveRolePermissions = async () => {
    if (!canEdit || !selectedRole) return;
    if (savingPermissions) return;

    setSavingPermissions(true);

    try {
      const matchingPermissions = permissions.filter(
        (permission) => permission.userType === selectedRole.userType,
      );

      const existingMappings = rolePermissions.filter(
        (mapping) =>
          mapping.roleId === selectedRole.id &&
          matchingPermissions.some(
            (permission) => permission.id === mapping.permissionId,
          ),
      );

      const selectedSet = new Set(selectedPermissionIds);

      const operations = [];

      for (const mapping of existingMappings) {
        const shouldBeAssigned = selectedSet.has(mapping.permissionId);

        if (!shouldBeAssigned) {
          operations.push(rolePermissionsApi.remove(mapping.id));
        } else if (mapping.isActive === false) {
          operations.push(
            rolePermissionsApi.update(mapping.id, {
              isActive: true,
            }),
          );
        }
      }

      const existingPermissionIds = new Set(
        existingMappings.map((mapping) => mapping.permissionId),
      );

      for (const permissionId of selectedPermissionIds) {
        if (!existingPermissionIds.has(permissionId)) {
          operations.push(
            rolePermissionsApi.create({
              roleId: selectedRole.id,
              permissionId,
            }),
          );
        }
      }

      await Promise.all(operations);

      notificationCtx.show({
        message: "Role permissions updated successfully.",
        stat: "success",
      });

      setPermissionModalOpen(false);
      setSelectedRole(null);
      setSelectedPermissionIds([]);

      await fetchAllData();
    } catch (error) {
      console.error("Error updating role permissions:", error);

      notificationCtx.show({
        message:
          error?.response?.data?.message ||
          "Failed to update role permissions.",
        stat: "error",
      });
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleDeleteRole = (id) => {
    if (!canEdit) return;
    const confirmDelete = async () => {
      try {
        await rolesApi.remove(id);

        setRoles((prev) => prev.filter((role) => role.id !== id));

        setRolePermissions((prev) => prev.filter((rp) => rp.roleId !== id));

        notificationCtx.show({
          message: "Role deleted successfully.",
          stat: "success",
        });
      } catch (error) {
        notificationCtx.show({
          message: error?.response?.data?.message || "Failed to delete role.",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this role?",
      stat: "warning",
      onConfirm: confirmDelete,
    });
  };

  const handleDeletePermission = (id) => {
    if (!canEdit) return;
    const confirmDelete = async () => {
      try {
        await permissionsApi.remove(id);

        setPermissions((prev) =>
          prev.filter((permission) => permission.id !== id),
        );

        setRolePermissions((prev) =>
          prev.filter((rp) => rp.permissionId !== id),
        );

        notificationCtx.show({
          message: "Permission deleted successfully.",
          stat: "success",
        });
      } catch (error) {
        notificationCtx.show({
          message:
            error?.response?.data?.message || "Failed to delete permission.",
          stat: "error",
        });
      }
    };

    notificationCtx.show({
      message: "Are you sure you want to delete this permission?",
      stat: "warning",
      onConfirm: confirmDelete,
    });
  };

  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);

  if (authLoading || permLoading || loading) {
    return (
      <div className="text-center">
        <Navbar />
        <Loader />
      </div>
    );
  }

  if (!isAuthenticated || !hasPermission) {
    return (
      <div className="text-center">
        <Navbar />
        <AccessDenied />
      </div>
    );
  }

  const modalPermissions = selectedRole
    ? permissions.filter(
        (permission) => permission.userType === selectedRole.userType,
      )
    : [];

  return (
    <div>
      <Navbar />

      <div
        className="d-flex flex-column"
        style={{
          minHeight: "0",
          height: "109vh",
          overflow: "hidden",
          padding: "10px 50px 0",
        }}
      >
        {/* PAGE HEADER */}
        <div
          className="d-flex justify-content-between align-items-end mb-5"
          style={{
            padding: "0 8px",
          }}
        >
          <div>
            <h1
              className="mb-1"
              style={{
                // fontSize: "28px",
                fontWeight: "700",
                color: "#302d45",
              }}
            >
              ROLE & PERMISSION MANAGEMENT
            </h1>

            <small
              style={{
                color: "#625d75",
                fontSize: "14px",
              }}
            >
              Manage roles, permissions, and access assignments
            </small>
          </div>
        </div>

        {/* ========================================================= */}
        {/* MAIN CONTENT */}
        {/* ========================================================= */}

        <div className="row g-4">
          {/* ========================================================= */}
          {/* ROLES */}
          {/* ========================================================= */}

          <div className="col-md-6">
            <div
              style={{
                overflow: "hidden",
                padding: "10px"
              }}
            >
              {/* TABLE HEADER */}
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#d5d1fd",
                  padding: "20px 24px",
                }}
              >
                <div>
                  <h3
                    className="fw-bold mb-1"
                    style={{
                      fontSize: "22px",
                    }}
                  >
                    Roles
                  </h3>

                  <small
                    style={{
                      color: "#625d75",
                    }}
                  >
                    {roles.length} {roles.length === 1 ? "role" : "roles"}{" "}
                    configured
                  </small>
                </div>

                {canEdit && (
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: "#ffffff",
                      borderRadius: "10px",
                      fontWeight: "600",
                    }}
                    onClick={() => {
                      resetRoleForm();
                      setShowRoleModal(true);
                    }}
                  >
                    + Add
                  </button>
                )}
              </div>

              {/* TABLE */}
              <div
                className="table-responsive"
                style={{
                  maxHeight: "100vh",
                  overflowY: "auto",
                }}
              >
                <table className="table align-middle mb-0">
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          backgroundColor: "#d5d1fd",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ROLE
                      </th>

                      <th
                        style={{
                          backgroundColor: "#d5d1fd",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        USER TYPE
                      </th>

                      <th
                        style={{
                          backgroundColor: "#d5d1fd",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        ACTIONS
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {roles.length === 0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center"
                          style={{
                            padding: "50px 20px",
                            color: "#777",
                          }}
                        >
                          <div className="fw-semibold mb-1">
                            No roles available
                          </div>

                          <small>Add a role to start managing access.</small>
                        </td>
                      </tr>
                    ) : (
                      roles.map((role) => (
                        <tr key={role.id}>
                          <td
                            className="text-center fw-semibold"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            {role.label}
                          </td>

                          <td
                            className="text-center"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor:
                                  role.userType === "admin"
                                    ? "#d5d1fd"
                                    : "#bff1db",
                                color: "#000",
                                padding: "7px 12px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                              }}
                            >
                              {role.userType}
                            </span>
                          </td>

                          <td
                            className="text-center"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            <div className="d-flex justify-content-center gap-2 flex-wrap">
                              {canEdit ? (
                                <>
                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#bff1db",
                                  borderColor: "#bff1db",
                                  fontWeight: "600",
                                  borderRadius: "9px",
                                }}
                                onClick={() => handleOpenPermissionModal(role)}
                              >
                                Assign Permissions
                              </button>

                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#f9e1be",
                                  borderColor: "#f9e1be",
                                  fontWeight: "600",
                                  borderRadius: "9px",
                                }}
                                onClick={() => {
                                  handleEditRole(role);
                                  setShowRoleModal(true);
                                }}
                              >
                                Edit
                              </button>

                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#f2c4c4",
                                  borderColor: "#f2c4c4",
                                  fontWeight: "600",
                                  borderRadius: "9px",
                                }}
                                onClick={() => handleDeleteRole(role.id)}
                              >
                                Delete
                              </button>
                                </>
                              ) : (
                                <span style={{ color: "#999" }}>—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ========================================================= */}
          {/* PERMISSIONS */}
          {/* ========================================================= */}

          <div className="col-md-6">
            <div
              style={{
                overflow: "hidden",
                padding: "10px"
              }}
            >
              {/* TABLE HEADER */}
              <div
                className="d-flex justify-content-between align-items-center"
                style={{
                  backgroundColor: "#bff1db",
                  padding: "20px 24px",
                }}
              >
                <div>
                  <h3
                    className="fw-bold mb-1"
                    style={{
                      fontSize: "22px",
                    }}
                  >
                    Permissions
                  </h3>

                  <small
                    style={{
                      color: "#625d75",
                    }}
                  >
                    {permissions.length}{" "}
                    {permissions.length === 1 ? "permission" : "permissions"}{" "}
                    configured
                  </small>
                </div>

                {canEdit && (
                  <button
                    className="btn btn-sm"
                    style={{
                      backgroundColor: "#ffffff",
                      borderColor: "#ffffff",
                      borderRadius: "10px",
                      fontWeight: "600",
                    }}
                    onClick={() => {
                      resetPermissionForm();
                      setShowPermissionModal(true);
                    }}
                  >
                    + Add
                  </button>
                )}
              </div>

              {/* TABLE */}
              <div
                className="table-responsive"
                style={{
                  maxHeight: "100vh",
                  overflowY: "auto",
                }}
              >
                <table className="table align-middle mb-0">
                  <thead
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 2,
                    }}
                  >
                    <tr>
                      <th
                        style={{
                          backgroundColor: "#bff1db",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                        }}
                      >
                        PERMISSION
                      </th>

                      <th
                        style={{
                          backgroundColor: "#bff1db",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                          whiteSpace: "nowrap",
                        }}
                      >
                        USER TYPE
                      </th>

                      <th
                        style={{
                          backgroundColor: "#bff1db",
                          textAlign: "center",
                          padding: "14px",
                          fontSize: "13px",
                          fontWeight: "700",
                        }}
                      >
                        ACTIONS
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {permissions.length === 0 ? (
                      <tr>
                        <td
                          colSpan="3"
                          className="text-center"
                          style={{
                            padding: "50px 20px",
                            color: "#777",
                          }}
                        >
                          <div className="fw-semibold mb-1">
                            No permissions available
                          </div>

                          <small>
                            Add a permission to define system access.
                          </small>
                        </td>
                      </tr>
                    ) : (
                      permissions.map((permission) => (
                        <tr key={permission.id}>
                          <td
                            className="text-center fw-semibold"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            {permission.label}
                          </td>

                          <td
                            className="text-center"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            <span
                              className="badge rounded-pill"
                              style={{
                                backgroundColor:
                                  permission.userType === "admin"
                                    ? "#d5d1fd"
                                    : "#bff1db",
                                color: "#000",
                                padding: "7px 12px",
                                fontWeight: "600",
                                textTransform: "capitalize",
                              }}
                            >
                              {permission.userType}
                            </span>
                          </td>

                          <td
                            className="text-center"
                            style={{
                              padding: "16px 12px",
                            }}
                          >
                            <div className="d-flex justify-content-center gap-2">
                              {canEdit ? (
                                <>
                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#f9e1be",
                                  borderColor: "#f9e1be",
                                  fontWeight: "600",
                                  borderRadius: "9px",
                                }}
                                onClick={() => {
                                  handleEditPermission(permission);
                                  setShowPermissionModal(true);
                                }}
                              >
                                Edit
                              </button>

                              <button
                                className="btn btn-sm"
                                style={{
                                  backgroundColor: "#f2c4c4",
                                  borderColor: "#f2c4c4",
                                  fontWeight: "600",
                                  borderRadius: "9px",
                                }}
                                onClick={() =>
                                  handleDeletePermission(permission.id)
                                }
                              >
                                Delete
                              </button>
                                </>
                              ) : (
                                <span style={{ color: "#999" }}>—</span>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* ========================================================= */}
        {/* ADD / EDIT ROLE MODAL */}
        {/* ========================================================= */}

        {showRoleModal && (
          <div
            className="modal fade show"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.7)",
              zIndex: 10500,
            }}
            tabIndex="-1"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{
                width: "100%",
                maxWidth: "650px",
                margin: "20px",
              }}
            >
              <div
                className="modal-content"
                style={{
                  border: "none",
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                {/* MODAL HEADER */}
                <div
                  className="modal-header"
                  style={{
                    backgroundColor: "#d5d1fd",
                    borderBottom: "none",
                    padding: "22px 26px",
                  }}
                >
                  <div>
                    <h4
                      className="modal-title fw-bold mb-1"
                      style={{
                        fontSize: "22px",
                      }}
                    >
                      {editingRoleId ? "EDIT ROLE" : "ADD ROLE"}
                    </h4>

                    <small
                      style={{
                        color: "#625d75",
                      }}
                    >
                      {editingRoleId
                        ? "Update the role configuration"
                        : "Create a new system role"}
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowRoleModal(false);
                      resetRoleForm();
                    }}
                    aria-label="Close"
                  />
                </div>

                {/* MODAL BODY */}
                <div
                  className="modal-body"
                  style={{
                    padding: "26px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <form
                    onSubmit={(e) => {
                      handleRoleSubmit(e);
                      setShowRoleModal(false);
                    }}
                  >
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Role name
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={roleForm.name}
                          onChange={handleRoleFieldChange}
                          placeholder="e.g. standard_admin"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Label</label>

                        <input
                          type="text"
                          className="form-control"
                          name="label"
                          value={roleForm.label}
                          onChange={handleRoleFieldChange}
                          placeholder="Display label"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          User type
                        </label>

                        <select
                          className="form-select"
                          name="userType"
                          value={roleForm.userType}
                          onChange={handleRoleFieldChange}
                        >
                          <option value="admin">Admin</option>
                          <option value="student">Student</option>
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Description
                        </label>

                        <textarea
                          className="form-control"
                          rows="3"
                          name="description"
                          value={roleForm.description}
                          onChange={handleRoleFieldChange}
                          placeholder="Describe the role"
                        />
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                      <button
                        type="submit"
                        className="btn flex-fill"
                        disabled={roleFormBusy}
                        style={{
                          backgroundColor: "#f2c4c4",
                          borderColor: "#f2c4c4",
                          color: "#000",
                          fontWeight: "600",
                          borderRadius: "10px",
                          padding: "11px",
                        }}
                      >
                        {roleFormBusy
                          ? "Saving..."
                          : editingRoleId
                            ? "UPDATE ROLE"
                            : "ADD ROLE"}
                      </button>

                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: "#f9e1be",
                          borderColor: "#f9e1be",
                          fontWeight: "600",
                          borderRadius: "10px",
                          padding: "11px 20px",
                        }}
                        onClick={() => {
                          setShowRoleModal(false);
                          resetRoleForm();
                        }}
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* ADD / EDIT PERMISSION MODAL */}
        {/* ========================================================= */}

        {showPermissionModal && (
          <div
            className="modal fade show"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "rgba(0,0,0,0.7)",
              zIndex: 10500,
            }}
            tabIndex="-1"
          >
            <div
              className="modal-dialog modal-dialog-centered"
              style={{
                width: "100%",
                maxWidth: "650px",
                margin: "20px",
              }}
            >
              <div
                className="modal-content"
                style={{
                  border: "none",
                  borderRadius: "20px",
                  overflow: "hidden",
                }}
              >
                {/* MODAL HEADER */}
                <div
                  className="modal-header"
                  style={{
                    backgroundColor: "#bff1db",
                    borderBottom: "none",
                    padding: "22px 26px",
                  }}
                >
                  <div>
                    <h4
                      className="modal-title fw-bold mb-1"
                      style={{
                        fontSize: "22px",
                      }}
                    >
                      {editingPermissionId
                        ? "EDIT PERMISSION"
                        : "ADD PERMISSION"}
                    </h4>

                    <small
                      style={{
                        color: "#625d75",
                      }}
                    >
                      {editingPermissionId
                        ? "Update the permission configuration"
                        : "Create a new system permission"}
                    </small>
                  </div>

                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowPermissionModal(false);
                      resetPermissionForm();
                    }}
                    aria-label="Close"
                  />
                </div>

                {/* MODAL BODY */}
                <div
                  className="modal-body"
                  style={{
                    padding: "26px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <form
                    onSubmit={(e) => {
                      handlePermissionSubmit(e);
                      setShowPermissionModal(false);
                    }}
                  >
                    <div className="row g-3">
                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Permission name
                        </label>

                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={permissionForm.name}
                          onChange={handlePermissionFieldChange}
                          placeholder="e.g. view_courses"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">Label</label>

                        <input
                          type="text"
                          className="form-control"
                          name="label"
                          value={permissionForm.label}
                          onChange={handlePermissionFieldChange}
                          placeholder="Friendly display label"
                          required
                        />
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          User type
                        </label>

                        <select
                          className="form-select"
                          name="userType"
                          value={permissionForm.userType}
                          onChange={handlePermissionFieldChange}
                        >
                          <option value="admin">Admin</option>
                          <option value="student">Student</option>
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label fw-semibold">
                          Description
                        </label>

                        <textarea
                          className="form-control"
                          rows="3"
                          name="description"
                          value={permissionForm.description}
                          onChange={handlePermissionFieldChange}
                          placeholder="Describe what this permission allows"
                        />
                      </div>
                    </div>

                    <div className="d-flex gap-2 mt-4">
                      <button
                        type="submit"
                        className="btn flex-fill"
                        disabled={permissionFormBusy}
                        style={{
                          backgroundColor: "#bff1db",
                          borderColor: "#bff1db",
                          color: "#000",
                          fontWeight: "600",
                          borderRadius: "10px",
                          padding: "11px",
                        }}
                      >
                        {permissionFormBusy
                          ? "Saving..."
                          : editingPermissionId
                            ? "UPDATE PERMISSION"
                            : "ADD PERMISSION"}
                      </button>

                      <button
                        type="button"
                        className="btn"
                        style={{
                          backgroundColor: "#f9e1be",
                          borderColor: "#f9e1be",
                          fontWeight: "600",
                          borderRadius: "10px",
                          padding: "11px 20px",
                        }}
                        onClick={() => {
                          setShowPermissionModal(false);
                          resetPermissionForm();
                        }}
                      >
                        CANCEL
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ============================================================= */}
      {/* ASSIGN PERMISSIONS MODAL */}
      {/* ============================================================= */}
      {permissionModalOpen && selectedRole && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1055,
            padding: "20px",
          }}
          onClick={handleClosePermissionModal}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "760px",
              maxHeight: "90vh",
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              boxShadow: "0 18px 50px rgba(0,0,0,0.22)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* ========================================================= */}
            {/* HEADER */}
            {/* ========================================================= */}
            <div
              className="d-flex align-items-center justify-content-between"
              style={{
                backgroundColor: "#d5d1fd",
                padding: "20px 24px",
                flexShrink: 0,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
              }}
            >
              <div>
                <h3
                  className="fw-bold mb-1"
                  style={{
                    fontSize: "23px",
                    letterSpacing: "-0.3px",
                  }}
                >
                  ASSIGN PERMISSIONS
                </h3>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: "600",
                      color: "#3f3b4d",
                    }}
                  >
                    {selectedRole.label || selectedRole.name}
                  </span>

                  <span
                    className="badge rounded-pill"
                    style={{
                      backgroundColor: "#ffffff",
                      color: "#000",
                      padding: "6px 11px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {selectedRole.userType}
                  </span>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                aria-label="Close"
                onClick={handleClosePermissionModal}
                disabled={savingPermissions}
              />
            </div>

            {/* ========================================================= */}
            {/* BODY */}
            {/* ========================================================= */}
            <div
              style={{
                padding: "22px 24px",
                overflowY: "auto",
                flex: "1 1 auto",
                minHeight: 0,
              }}
            >
              {/* ROLE INFORMATION */}
              <div
                className="mb-4"
                style={{
                  backgroundColor: "#ffffff",
                  borderRadius: "14px",
                  padding: "16px 18px",
                  border: "1px solid rgba(0,0,0,0.06)",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                }}
              >
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <h6
                    className="mb-0 fw-bold"
                    style={{
                      fontSize: "15px",
                    }}
                  >
                    Permissions for this role
                  </h6>

                  <span
                    style={{
                      backgroundColor: "#d5d1fd",
                      borderRadius: "20px",
                      padding: "5px 10px",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {selectedPermissionIds.length} selected
                  </span>
                </div>

                {selectedRole.description && (
                  <p
                    className="mb-0"
                    style={{
                      color: "#625d75",
                      fontSize: "13px",
                      lineHeight: "1.5",
                    }}
                  >
                    {selectedRole.description}
                  </p>
                )}
              </div>

              {/* PERMISSIONS */}
              {modalPermissions.length === 0 ? (
                <div
                  className="text-center"
                  style={{
                    backgroundColor: "#ffffff",
                    borderRadius: "15px",
                    padding: "45px 20px",
                    color: "#625d75",
                  }}
                >
                  <h6 className="fw-semibold mb-2">No permissions available</h6>

                  <small>
                    There are no permissions configured for this user type.
                  </small>
                </div>
              ) : (
                <>
                  <div className="d-flex align-items-center justify-content-between mb-3">
                    <div>
                      <h6
                        className="fw-bold mb-1"
                        style={{
                          fontSize: "16px",
                        }}
                      >
                        Available Permissions
                      </h6>

                      <small
                        style={{
                          color: "#625d75",
                        }}
                      >
                        Select the permissions this role should have
                      </small>
                    </div>

                    <small
                      style={{
                        color: "#625d75",
                        fontWeight: "600",
                      }}
                    >
                      {modalPermissions.length} available
                    </small>
                  </div>

                  <div className="d-flex flex-column gap-2">
                    {modalPermissions.map((permission) => {
                      const isChecked = selectedPermissionIds.includes(
                        permission.id,
                      );

                      return (
                        <label
                          key={permission.id}
                          className="d-flex align-items-start"
                          style={{
                            cursor: savingPermissions ? "default" : "pointer",
                            backgroundColor: isChecked ? "#d5d1fd" : "#ffffff",
                            border: isChecked
                              ? "1px solid #d5d1fd"
                              : "1px solid #e4dfd8",
                            borderRadius: "13px",
                            padding: "13px 15px",
                            transition: "all 0.15s ease",
                            boxShadow: isChecked
                              ? "0 2px 7px rgba(100,90,180,0.08)"
                              : "0 1px 4px rgba(0,0,0,0.03)",
                          }}
                        >
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={isChecked}
                            disabled={savingPermissions}
                            onChange={() =>
                              handlePermissionCheckboxChange(permission.id)
                            }
                            style={{
                              marginTop: "2px",
                              marginRight: "14px",
                              cursor: savingPermissions ? "default" : "pointer",
                            }}
                          />

                          <div
                            className="flex-grow-1"
                            style={{
                              minWidth: 0,
                            }}
                          >
                            <div
                              className="fw-semibold"
                              style={{
                                fontSize: "14px",
                                color: "#292631",
                                lineHeight: "1.4",
                              }}
                            >
                              {permission.label || permission.name}
                            </div>

                            {permission.description && (
                              <div
                                style={{
                                  fontSize: "12px",
                                  color: "#625d75",
                                  lineHeight: "1.45",
                                  marginTop: "3px",
                                }}
                              >
                                {permission.description}
                              </div>
                            )}
                          </div>

                          {isChecked && (
                            <span
                              style={{
                                backgroundColor: "#d5d1fd",
                                borderRadius: "20px",
                                padding: "4px 8px",
                                fontSize: "10px",
                                fontWeight: "700",
                                marginLeft: "10px",
                                flexShrink: 0,
                              }}
                            >
                              SELECTED
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            {/* ========================================================= */}
            {/* FOOTER */}
            {/* ========================================================= */}
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                backgroundColor: "#ffffff",
                padding: "15px 24px",
                borderTop: "1px solid #e4dfd8",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  color: "#625d75",
                  fontSize: "13px",
                }}
              >
                <strong style={{ color: "#292631" }}>
                  {selectedPermissionIds.length}
                </strong>{" "}
                permission
                {selectedPermissionIds.length !== 1 ? "s" : ""} selected
              </div>

              <div className="d-flex gap-2">
                <button
                  type="button"
                  className="btn"
                  onClick={handleClosePermissionModal}
                  disabled={savingPermissions}
                  style={{
                    backgroundColor: "#f9e1be",
                    borderColor: "#f9e1be",
                    color: "#000",
                    fontWeight: "600",
                    borderRadius: "10px",
                    padding: "9px 18px",
                  }}
                >
                  CANCEL
                </button>

                <button
                  type="button"
                  className="btn"
                  onClick={handleSaveRolePermissions}
                  disabled={savingPermissions || !canEdit}
                  style={{
                    backgroundColor: "#bff1db",
                    borderColor: "#bff1db",
                    color: "#000",
                    fontWeight: "700",
                    borderRadius: "10px",
                    padding: "9px 20px",
                    minWidth: "165px",
                    display: canEdit ? undefined : "none",
                  }}
                >
                  {savingPermissions ? "SAVING..." : "SAVE PERMISSIONS"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
